import { useMemo } from "react";
import { AskMonitoringChat } from "./AskMonitoringChat";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { Button } from "../Button";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { SearchField } from "../Settings/shared";
import { Sparkline } from "../Sparkline";
import { META_SEP } from "../MetaSeparator";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  formatFailureDuration,
  sortFailingWorkflows,
  type FailingWorkflowSort,
} from "./failingWorkflows";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
  SummaryCard,
} from "./MonitoringDetailCard";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { PanelSkeleton } from "./workflowsLayout";

function parseSort(value: string | undefined): FailingWorkflowSort {
  return value === "volume" ? "volume" : "duration";
}

const GHOST_FAILURE_TREND = [2, 4, 3, 6, 4, 7, 5, 9, 6, 8, 5, 7];

export function FailingWorkflowsPanel() {
  const makeUrl = useMakeUrl();
  const recordInteraction = useMonitoringInteraction();
  const {
    sort: sortParam,
    panel,
    search: searchParam,
  } = useCurrentUrl(routes.monitoring.workflows);
  const sort = parseSort(sortParam);
  const search = searchParam ?? "";
  const open = panel === "failing";
  const setSearch = (value: string) =>
    navigate(routes.monitoring.workflows, { search: value }, { replace: true });
  const close = () =>
    navigate(
      routes.monitoring.workflows,
      { panel: "", search: "" },
      { replace: true }
    );
  const { report, loading } = useMonitoringWorkflows();
  const { spaceById, loading: spacesLoading } = useMonitoringSpaces();
  const { matchesSpace } = useMonitoringScope(routes.monitoring.workflows);
  const displayName = useSpaceDisplayName();

  const workflowById = useMemo(() => {
    const map = new Map<string, (typeof report.workflows)[number]>();
    for (const workflow of report.workflows) {
      map.set(workflow.id, workflow);
    }
    return map;
  }, [report]);

  const failingWorkflows = useMemo(
    () =>
      sortFailingWorkflows(
        report.failing.filter((row) => matchesSpace(row.spaceId)),
        sort
      ),
    [report, sort, matchesSpace]
  );

  const setSort = (next: FailingWorkflowSort) => {
    recordInteraction("workflows.sort_change", { value: next });
    navigate(routes.monitoring.workflows, { sort: next }, { replace: true });
  };

  const count = failingWorkflows.length;

  const displayedWorkflows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return failingWorkflows;
    return failingWorkflows.filter((row) => row.name.toLowerCase().includes(q));
  }, [failingWorkflows, search]);

  const failingSparkline =
    count > 0 ? (
      <Tooltip content={`Failed runs${META_SEP}last 30 days`}>
        <div style={{ display: "flex", flexShrink: 0 }}>
          <Sparkline values={report.failureTrend} color="var(--red-600)" />
        </div>
      </Tooltip>
    ) : (
      <div style={{ display: "flex", flexShrink: 0 }}>
        <Sparkline values={GHOST_FAILURE_TREND} color="var(--gray-400)" />
      </div>
    );

  if (loading || spacesLoading) {
    return <PanelSkeleton />;
  }

  return (
    <>
      <SummaryCard
        title="Failing workflows"
        cornerAction={
          <AskMonitoringChat
            surface="failures"
            label="Investigate in Chat"
            snapshot={`${count} workflows currently failing`}
          />
        }
        value={count}
        valueLabel="need attention"
        onOpen={
          count > 0
            ? () => {
                recordInteraction("workflows.panel_drilldown_open", {
                  value: "failing",
                });
                navigate(routes.monitoring.workflows, {
                  panel: "failing",
                  search: "",
                });
              }
            : undefined
        }
        accessory={failingSparkline}
      />

      <Modal open={open} onClose={close} style={DETAIL_MODAL_STYLE}>
        <ModalHeader>
          <ModalTitle icon="traffic-cone" title="Failing workflows" />
          <ModalHeaderRight>
            <AskMonitoringChat
              surface="failures"
              label="Investigate in Chat"
              alwaysVisible
              inline
              includeSearch
              snapshot={`${displayedWorkflows.length} failing workflows in the filtered table`}
            />
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody style={{ gap: 0 }}>
          <ListSection
            title="Failing workflows"
            description="Workflows with repeated failures on the live branch."
            action={
              <SearchField
                placeholder="Search workflows…"
                value={search}
                onChange={setSearch}
                width={280}
                autoFocus
              />
            }
          >
            <ListTable
              headers={{
                name: "Workflow",
                duration: "Failing for",
                volume: "Failed (7d)",
              }}
              columns={{ name: "flex", duration: "md", volume: "md" }}
              sortableColumns={["duration", "volume"]}
              sortColumn={sort}
              sortDirection="desc"
              onSort={(col) => setSort(col as FailingWorkflowSort)}
              emptyState={
                <Text variant="text-regular" color="var(--text-secondary)">
                  {search
                    ? "No workflows match your search."
                    : "Nothing failing."}
                </Text>
              }
              items={displayedWorkflows.map((row) => {
                const workflow = workflowById.get(row.workflowId);
                const space = spaceById.get(row.spaceId);
                return {
                  key: row.workflowId,
                  link: makeUrl(routes.space.workflow, {
                    spaceId: row.spaceId,
                    workflowId: row.workflowId,
                  }),
                  onNavigate: () =>
                    recordInteraction("workflows.workflow_link_click", {
                      targetType: "workflow",
                      targetId: row.workflowId,
                    }),
                  name: (hover: boolean) => (
                    <NameCell>
                      {workflow ? (
                        <WorkflowBadge
                          workflow={{
                            id: workflow.id,
                            badgeSeed: workflow.badgeSeed,
                            pausedAt: workflow.pausedAt,
                            branches: [{ name: "main" }],
                          }}
                          hover={hover}
                        />
                      ) : null}
                      <NameBody>
                        <Text variant="text-regular-md" ellipsis>
                          {row.name}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {space ? displayName(space) : ""}
                        </Text>
                      </NameBody>
                    </NameCell>
                  ),
                  duration: (
                    <Text variant="text-small">
                      {formatFailureDuration(row.failureDurationMs)}
                    </Text>
                  ),
                  volume: (
                    <Text variant="text-small">
                      {row.failedRunCount} / {row.totalRunCount}
                    </Text>
                  ),
                };
              })}
            />
          </ListSection>
        </ModalBody>

        <ModalFooter>
          <Text variant="text-small" color="var(--text-secondary)">
            {count} need attention
          </Text>
          <Button size="l" variant="primary" onClick={close}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
